import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Button } from '@/components/ui/button'
import { Icon } from '@/components/ui/icon'
import { Input } from '@/components/ui/input'
import {
  DEFAULT_SHADER_MODELS,
  loadShaderProviderDefaults,
  saveShaderProviderDefaults,
  shaderProviderReadiness,
} from '@/lib/canvas/shader/authoring'
import { useShaderCodeModelHost } from '@/lib/canvas/shader/authoring/host-context'
import {
  DEFAULT_LOCAL_BASE_URL,
  SHADER_PROVIDER_IDS,
  SHADER_PROVIDER_LABELS,
  type ShaderProviderConfig,
  type ShaderProviderId,
} from '@/lib/canvas/shader/authoring/types'
import { trustLocalServer } from './node-provider'
import { SegmentedChoice } from './SegmentedChoice'
import { ShaderModelPicker } from './ShaderModelPicker'

const NO_SECRETS = {}

/** A starting config when the user switches provider: their default if it is for that provider, else the provider's default model. */
export function configForProvider(provider: ShaderProviderId): ShaderProviderConfig {
  const defaults = loadShaderProviderDefaults()
  if (defaults.provider === provider) return defaults
  return {
    provider,
    model: DEFAULT_SHADER_MODELS[provider],
    ...(provider === 'openrouter' ? { billing: 'genie-credits' as const } : {}),
    ...(provider === 'local' ? { baseUrl: DEFAULT_LOCAL_BASE_URL } : {}),
  }
}

interface ShaderProviderSettingsProps {
  /** This node's choice (checked by resolveShaderProviderConfig), or the user's default when it has none. */
  config: ShaderProviderConfig
  /** Saves the (non-secret) choice on the node. */
  onChange: (config: ShaderProviderConfig) => void
}

/** Who writes this node's shaders: provider, model, how it is paid for and where the key lives. */
export function ShaderProviderSettings({ config, onChange }: ShaderProviderSettingsProps) {
  const host = useShaderCodeModelHost()
  const secrets = NO_SECRETS
  const providerOptions = SHADER_PROVIDER_IDS.filter(value => value !== 'grok' || host?.providers.includes('grok')).map(value => ({ value, label: SHADER_PROVIDER_LABELS[value] }))
  const [savedDefault, setSavedDefault] = useState(false)
  const savedTimer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined)
  const [baseUrl, setBaseUrl] = useState(config.baseUrl ?? DEFAULT_LOCAL_BASE_URL)
  useEffect(() => setBaseUrl(config.baseUrl ?? DEFAULT_LOCAL_BASE_URL), [config.baseUrl])
  useEffect(() => () => clearTimeout(savedTimer.current), [])

  const hostServes = host?.providers.includes(config.provider) ?? false
  const billing = config.billing ?? 'genie-credits'
  const readiness = useMemo(() => shaderProviderReadiness(config, secrets, host), [config, secrets, host])

  const handleProvider = useCallback((provider: ShaderProviderId) => {
    if (provider !== config.provider) onChange(configForProvider(provider))
  }, [config.provider, onChange])
  const useManagedBilling = useCallback(() => onChange({ ...config, billing: 'genie-credits' }), [config, onChange])
  const handleModel = useCallback((model: string) => onChange({ ...config, model }), [config, onChange])

  const handleBaseUrlChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => setBaseUrl(e.target.value), [])
  const commitBaseUrl = useCallback(() => {
    const next = baseUrl.trim() || DEFAULT_LOCAL_BASE_URL
    if (next === config.baseUrl) return
    // Typed here, by this user: the node may send prompts to it from now on.
    trustLocalServer(next)
    onChange({ ...config, baseUrl: next })
  }, [baseUrl, config, onChange])
  const handleBaseUrlKey = useCallback((e: React.KeyboardEvent<HTMLInputElement>) => {
    e.stopPropagation()
    if (e.key === 'Enter') commitBaseUrl()
  }, [commitBaseUrl])

  const handleMakeDefault = useCallback(() => {
    saveShaderProviderDefaults(config)
    setSavedDefault(true)
    clearTimeout(savedTimer.current)
    savedTimer.current = setTimeout(() => setSavedDefault(false), 1500)
  }, [config])

  return (
    <div className="flex w-[336px] flex-col gap-3 px-2 py-2">
      <SegmentedChoice label="Provider" value={config.provider} options={providerOptions} onChange={handleProvider} />

      {config.provider === 'openrouter' && !hostServes && (
        billing === 'own-key'
          ? <Button size="sm" variant="outline" onClick={useManagedBilling}>Use Magic Lamp with Genie credits</Button>
          : <p className="text-xs text-muted-foreground">Uses Genie credits. Genie manages the connection; no personal key is needed.</p>
      )}

      <ShaderModelPicker config={config} secrets={secrets} onChange={handleModel} />

      {config.provider === 'local' && (
        <div className="flex flex-col gap-1">
          <label htmlFor="shader-local-base-url" className="text-xs">Server</label>
          <Input
            id="shader-local-base-url"
            inputSize="sm"
            spellCheck={false}
            value={baseUrl}
            onChange={handleBaseUrlChange}
            onBlur={commitBaseUrl}
            onKeyDown={handleBaseUrlKey}
          />
        </div>
      )}

      {hostServes ? (
        <p className="text-[10px] text-muted-foreground">This app connects to {SHADER_PROVIDER_LABELS[config.provider]} for you; no key needed.</p>
      ) : null}

      <div className="flex items-start gap-1.5 text-[10px]" role="status">
        <Icon name={readiness.ready ? 'check' : 'info'} className={readiness.ready ? 'mt-px h-3 w-3 text-emerald-600 dark:text-emerald-400' : 'mt-px h-3 w-3 text-amber-600 dark:text-amber-400'} />
        <span className={readiness.ready ? 'text-muted-foreground' : 'text-amber-700 dark:text-amber-200'}>
          {readiness.ready ? `Ready - ${SHADER_PROVIDER_LABELS[config.provider]} writes this shader.` : readiness.reason}
        </span>
      </div>

      <Button size="sm" variant="outline" onClick={handleMakeDefault} className="h-7 w-full text-[11px]">
        {savedDefault ? 'Saved as default' : 'Make default for new shaders'}
      </Button>
    </div>
  )
}
